import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { api, CONTACT_EMAIL } from "../config";

const SERVICE_OPTIONS = [
  "Patios & pavers",
  "Masonry",
  "Retaining walls",
  "Drainage",
  "Landscape design",
  "Lawn & maintenance",
  "Outdoor kitchens",
  "Pool landscaping",
  "Irrigation",
  "Other",
];

const INITIAL = {
  name: "",
  company: "",
  phone: "",
  email: "",
  website: "",
  services: [],
  message: "",
  website_url: "",
};

function formatPhoneInput(value) {
  let digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length > 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return digits;
}

function validate(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = "Please enter your name.";
  if (!values.company.trim()) errors.company = "Please enter your company name.";
  const digits = values.phone.replace(/\D/g, "");
  if (digits.length < 7) errors.phone = "Please enter a valid phone number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()))
    errors.email = "Please enter a valid email address.";
  if (values.website.trim() && !/\./.test(values.website.trim()))
    errors.website = "Enter a valid website or leave this blank.";
  if (values.message.length > 2000)
    errors.message = "Please keep this under 2,000 characters.";
  return errors;
}

function Field({ id, label, required, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="cr-label">
        {label}
        {required && <span className="text-accent"> *</span>}
      </label>
      {children}
      {error && (
        <p className="field-error" id={`${id}-error`} role="alert" data-testid={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function LeadForm() {
  const [values, setValues] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const toggleService = (option) => {
    setValues((v) => ({
      ...v,
      services: v.services.includes(option)
        ? v.services.filter((s) => s !== option)
        : [...v.services, option],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(api("/leads"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          source_page: window.location.pathname || "/",
        }),
      });
      if (res.status === 201) {
        setStatus("success");
        return;
      }
      if (res.status === 429) {
        setServerError("You're sending requests too quickly. Please wait a little and try again.");
      } else {
        setServerError(
          `Something went wrong. Please try again or email me directly at ${CONTACT_EMAIL}.`
        );
      }
      setStatus("error");
    } catch {
      setServerError(
        `Something went wrong. Please try again or email me directly at ${CONTACT_EMAIL}.`
      );
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="card-cr flex h-full flex-col items-start justify-center gap-5 p-8 sm:p-10"
        data-testid="lead-form-success"
      >
        <CheckCircle2 className="h-10 w-10 text-ok" />
        <h3 className="font-serif text-2xl text-cream sm:text-3xl">
          Thanks, your request is in.
        </h3>
        <p className="text-dim">
          I&rsquo;ll get back to you within one business day.
        </p>
        <Link to="/demos" className="btn-secondary mt-2" data-testid="success-view-demos">
          See sample sites
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="card-cr p-6 sm:p-8" data-testid="lead-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="lead-name" label="Your name" required error={errors.name}>
          <input
            id="lead-name"
            type="text"
            className="cr-input"
            autoComplete="name"
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "lead-name-error" : undefined}
            data-testid="lead-name-input"
          />
        </Field>
        <Field id="lead-company" label="Company name" required error={errors.company}>
          <input
            id="lead-company"
            type="text"
            className="cr-input"
            autoComplete="organization"
            value={values.company}
            onChange={(e) => set("company", e.target.value)}
            aria-invalid={!!errors.company}
            aria-describedby={errors.company ? "lead-company-error" : undefined}
            data-testid="lead-company-input"
          />
        </Field>
        <Field id="lead-phone" label="Phone" required error={errors.phone}>
          <input
            id="lead-phone"
            type="tel"
            inputMode="tel"
            className="cr-input"
            autoComplete="tel"
            placeholder="(555) 123-4567"
            value={values.phone}
            onChange={(e) => set("phone", formatPhoneInput(e.target.value))}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "lead-phone-error" : undefined}
            data-testid="lead-phone-input"
          />
        </Field>
        <Field id="lead-email" label="Email" required error={errors.email}>
          <input
            id="lead-email"
            type="email"
            className="cr-input"
            autoComplete="email"
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "lead-email-error" : undefined}
            data-testid="lead-email-input"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field id="lead-website" label="Current website (if any)" error={errors.website}>
            <input
              id="lead-website"
              type="text"
              className="cr-input"
              placeholder="Leave blank if you don't have one"
              value={values.website}
              onChange={(e) => set("website", e.target.value)}
              aria-invalid={!!errors.website}
              aria-describedby={errors.website ? "lead-website-error" : undefined}
              data-testid="lead-website-input"
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <span className="cr-label" id="lead-services-label">
            Main services
          </span>
          <div
            className="flex flex-wrap gap-2.5"
            role="group"
            aria-labelledby="lead-services-label"
            data-testid="lead-services-chips"
          >
            {SERVICE_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => toggleService(option)}
                aria-pressed={values.services.includes(option)}
                className={`chip ${values.services.includes(option) ? "chip-active" : ""}`}
                data-testid={`service-chip-${option.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <Field id="lead-message" label="Anything else I should know?" error={errors.message}>
            <textarea
              id="lead-message"
              rows={4}
              className="cr-textarea"
              value={values.message}
              onChange={(e) => set("message", e.target.value)}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? "lead-message-error" : undefined}
              data-testid="lead-message-input"
            />
          </Field>
        </div>
      </div>

      <div className="hidden" aria-hidden="true">
        <label htmlFor="lead-website-url">Leave this field empty</label>
        <input
          id="lead-website-url"
          type="text"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          value={values.website_url}
          onChange={(e) => set("website_url", e.target.value)}
        />
      </div>

      {status === "error" && serverError && (
        <div
          className="mt-5 flex items-start gap-3 rounded-[10px] border border-bad/40 bg-bad/10 p-4 text-sm text-cream"
          role="alert"
          data-testid="lead-form-error"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-bad" />
          <span>
            {serverError.split(CONTACT_EMAIL)[0]}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline underline-offset-2">
              {CONTACT_EMAIL}
            </a>
          </span>
        </div>
      )}

      <button
        type="submit"
        className="btn-primary mt-6 w-full sm:w-auto"
        disabled={status === "sending"}
        data-testid="lead-form-submit"
      >
        {status === "sending" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending&hellip;
          </>
        ) : (
          "Send request"
        )}
      </button>
    </form>
  );
}
